import type { WebsiteBlogPost } from '@/lib/contentService';

import { BLOG_TR_SLUG_BY_EN as SHARED_BLOG_TR_SLUG_BY_EN } from './blogSlugs.shared.js';
import { BLOG_TAG_SLUGS, getBlogTagPage } from './blogTagSlugs.shared.js';
import { canonicalBlogTagSlug, normalizeBlogTagSlug } from './blogTags.shared.js';

const LINKABLE_BLOG_TAG_SLUGS = new Set<string>(BLOG_TAG_SLUGS);

export { canonicalBlogTagSlug, normalizeBlogTagSlug };

/** Canonical EN slug → Turkish public slug (technical terms preserved where natural). */
export const BLOG_TR_SLUG_BY_EN: Record<string, string> = SHARED_BLOG_TR_SLUG_BY_EN;

const TR_SLUG_TO_EN = Object.fromEntries(
  Object.entries(BLOG_TR_SLUG_BY_EN).map(([en, tr]) => [tr, en]),
);

export type BlogSlugLocale = 'tr' | 'en';

export function getBlogCanonicalSlug(urlSlug: string): string {
  return TR_SLUG_TO_EN[urlSlug] ?? urlSlug;
}

export function getBlogPublicSlug(canonicalSlug: string, locale: BlogSlugLocale): string {
  if (locale === 'tr') {
    return BLOG_TR_SLUG_BY_EN[canonicalSlug] ?? canonicalSlug;
  }
  return canonicalSlug;
}

export function getBlogLogicalPath(post: Pick<WebsiteBlogPost, 'slug'>, locale: BlogSlugLocale): string {
  return `/blog/${getBlogPublicSlug(post.slug, locale)}`;
}

export function buildBlogTrSlugRedirectLines(): string[] {
  return Object.entries(BLOG_TR_SLUG_BY_EN).flatMap(([enSlug, trSlug]) => {
    if (enSlug === trSlug) return [];
    return [
      `/tr/blog/${enSlug}       /tr/blog/${trSlug}       301`,
      `/tr/blog/${enSlug}/      /tr/blog/${trSlug}       301`,
    ];
  });
}

/** Only tags with a prerendered listing page (same list as sitemap/prerender) may be linked. */
export function getBlogTagLinkPath(tag: string): string | null {
  const slug = canonicalBlogTagSlug(tag);
  return LINKABLE_BLOG_TAG_SLUGS.has(slug) ? `/blog/tag/${slug}` : null;
}

/** First raw spelling per canonical tag, so merged synonyms on one post render a single chip. */
export function uniqueBlogTags(tags: string[]): string[] {
  const seen = new Set<string>();
  return tags.filter((tag) => {
    const slug = canonicalBlogTagSlug(tag);
    if (!slug || seen.has(slug)) return false;
    seen.add(slug);
    return true;
  });
}

/** Locale label of the tag's canonical page; raw spelling when the tag has no page. */
export function getBlogTagLabel(tag: string, locale: BlogSlugLocale): string {
  const page = getBlogTagPage(canonicalBlogTagSlug(tag));
  return page ? page.label[locale] : formatBlogTagLabel(tag);
}

export function formatBlogTagLabel(tag: string): string {
  const trimmed = tag.trim();
  if (!trimmed) return trimmed;
  if (trimmed.toLowerCase() === 'ai') return 'AI';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}
